import {
  IntervalType,
  PaymentDetails,
  PaymentMethod,
} from "api/resources/suraasaPlus/types"
import { addDays, addMonths, addWeeks, addYears, format } from "date-fns"

import netBankingIcon from "./assets/bank.webp"
import creditCardIcon from "./assets/credit-card.webp"
import EMIIcon from "./assets/emi.webp"
import masterCardIcon from "./assets/master-card.webp"
import payLaterIcon from "./assets/paylater.webp"
import upiIcon from "./assets/upi.webp"
import visaIcon from "./assets/visa.webp"
import walletIcon from "./assets/wallet.webp"

export enum Network {
  visa = "visa",
  masterCard = "mastercard",
}

type PaymentStringResult = {
  label: string
  icon: string
}

const MASKED_DEFAULT = "xxxxxxxxxxxxxx"

export const getPaymentMethodString = ({
  method,
  identifier,
  issuer,
  network,
  fullVersion = false,
}: {
  method?: PaymentMethod
  identifier?: string
  issuer?: string
  network?: Network | string
  fullVersion?: boolean
}): PaymentStringResult => {
  if (!method) {
    return { icon: creditCardIcon, label: MASKED_DEFAULT }
  }

  switch (method) {
    case PaymentMethod.card: {
      const normalized = network?.toLowerCase()
      const icon =
        normalized === Network.visa
          ? visaIcon
          : normalized === Network.masterCard
          ? masterCardIcon
          : creditCardIcon
      return {
        icon,
        label: fullVersion
          ? `•••• •••• •••• ${identifier}`
          : identifier || MASKED_DEFAULT,
      }
    }

    case PaymentMethod.upi:
      return { icon: upiIcon, label: identifier || MASKED_DEFAULT }

    case PaymentMethod.netbanking:
      return { icon: netBankingIcon, label: issuer || "NetBanking" }

    case PaymentMethod.wallet:
      return { icon: walletIcon, label: issuer || "Wallet" }

    case PaymentMethod.paylater:
      return { icon: payLaterIcon, label: issuer || "Pay Later" }

    case PaymentMethod.emi:
      return { icon: EMIIcon, label: "xxxxxxxxxxxxxx" }

    default:
      return { icon: creditCardIcon, label: MASKED_DEFAULT }
  }
}

const intervalMap: Record<IntervalType, (date: Date) => Date> = {
  day: d => addDays(d, 1),
  week: d => addWeeks(d, 1),
  month: d => addMonths(d, 1),
  year: d => addYears(d, 1),
}

export function getTimeSpan({
  startDate,
  intervalType,
  nextBillDate = false,
}: {
  startDate?: string
  intervalType?: IntervalType
  nextBillDate?: boolean
}): string {
  if (!intervalType || !startDate) {
    return "Your subscription period is not set"
  }

  const start = new Date(startDate)
  // applied formula: transaction date + interval
  const end = intervalMap[intervalType](start)

  const formattedStart = format(start, "d MMM yyyy")
  const formattedEnd = format(end, "d MMM yyyy")

  if (nextBillDate) {
    const nextBilling = end
    const formattedNextBill = format(nextBilling, "d MMMM yyyy")
    return formattedNextBill
  }

  return `(${formattedStart} - ${formattedEnd})`
}

export function getPlanString(interval?: IntervalType): string {
  switch (interval) {
    case IntervalType.month:
      return "Monthly Payment Plan"
    case IntervalType.year:
      return "Annual Payment Plan"
    case IntervalType.week:
      return "Weekly Payment Plan"
    case IntervalType.day:
      return "Daily Payment Plan"
    default:
      return "Subscription Plan"
  }
}

export function getPaymentDetailsObject(paymentDetails: PaymentDetails | null) {
  let identifier = ""
  let issuer = ""
  let network = ""

  if (paymentDetails === null) return { identifier, issuer, network }

  const method = paymentDetails?.method

  if (method === PaymentMethod.upi || method === PaymentMethod.card) {
    identifier = paymentDetails?.identifier || ""
  }

  if (method === PaymentMethod.card) {
    network = paymentDetails?.network || ""
  }

  if (
    method === PaymentMethod.netbanking ||
    method === PaymentMethod.wallet ||
    method === PaymentMethod.paylater
  ) {
    issuer = paymentDetails?.issuer || ""
  }

  return { identifier, network, issuer }
}
